import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { memo, useState } from 'react'
import { MediaImageStandard, LinkButton, Typography } from '@/components/Contentful'

const EASE = 'transition-all duration-500 ease-in-out'

function Scene({ item, active }) {
  const { logo, title } = item || {}
  return (
    <div
      className={`absolute inset-0 ${EASE} ${
        active
          ? 'translate-y-0 scale-100 opacity-100'
          : 'pointer-events-none translate-y-4 scale-[0.98] opacity-0'
      }`}
      aria-hidden={!active}
    >
      {logo && (
        <MediaImageStandard
          content={{ url: logo }}
          layout="fill"
          objectFit="contain"
          fillRelativeWrapper={false}
          priority
          alt={title}
        />
      )}
    </div>
  )
}

function AccordionItem({ item, open, onToggle, pos }) {
  return (
    <div
      className={`border-b border-border-primary py-8 first:pt-0 ${
        pos !== 0 && 'border-t border-solid border-border-primary border-b-0 border-x-0'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="group flex w-full items-start justify-between gap-6 text-left bg-transparent border-none p-0"
      >
        <span
          className={`headingMd md:headingLg font-bold leading-[130%] ${EASE} ${
            open ? 'text-content-primary' : 'text-content-secondary hover:text-content-primary'
          }`}
        >
          {item?.title}
        </span>
        <span
          className={`relative mt-1.5 block h-4 w-4 shrink-0 ${
            open ? 'text-content-primary' : 'text-content-secondary'
          }`}
        >
          <span className="absolute left-0 top-1/2 h-[2px] w-4 -translate-y-1/2 rounded-full bg-current" />
          <span
            className={`absolute left-1/2 top-0 h-4 w-[2px] -translate-x-1/2 rounded-full bg-current ${EASE} ${
              open ? 'scale-y-0 opacity-0' : 'scale-y-100 opacity-100'
            }`}
          />
        </span>
      </button>

      <div
        className={`grid ${EASE} ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="m-0 pt-4 md:text-lg text-base leading-[1.3] text-content-primary sm:max-w-[581px] lg:max-w-[488px]">
            {item?.description}
            <div className="block lg:hidden [&_img]:object-left [&_img]:h-auto [&_img]:max-h-[484px] pt-4">
              <MediaImageStandard
                content={{ url: item?.logo }}
                objectFit="contain"
                width={508}
                height={484}
                fillRelativeWrapper={false}
                priority
                alt={item?.title}
              />
            </div>
            {item?.ctaUrl && item?.ctaText && (
              <div className="pt-8 [&_.button]:z-1 [&_.button]:mx-[2.4px] [&_.button]:my-0 [&_.button]:px-6 [&_.button]:py-3 xl:flex xl:m-0.5 xl:[&_.button]:py-2 xl:[&_.button]:px-5 breakpointLg:[&_.button]:mx-0">
                <LinkButton
                  className="button w-full lg:w-auto [&_p]:font-bold [&.button]:bg-blue-400"
                  content={{ linkHref: item?.ctaUrl, linkText: item?.ctaText }}
                  color="primary"
                  data-testid="simple-accordion-button"
                  track={{
                    ...{
                      click_id: item?.ctaText,
                      list_name: 'Simple Accordion CTA',
                      nav_link_section: 'Simple Accordion',
                      track_event: 'internal_campaign_click',
                      click_type: 'Button Click',
                      event_action: 'button_click',
                      event_type: 'button_click',
                    },
                    ...(item?.event || {}),
                  }}
                />
              </div>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

const SimpleAccordionModule = memo(function SimpleAccordionModule({ config }) {
  const { title, subTitle, items } = config || {}
  const [activeIndex, setActiveIndex] = useState(0)
  return (
    <Section
      disableGutters={true}
      data-testid="grid-section"
      className="py-[48px] px-[20px] breakpointMd:py-[80px] breakpointMd:px-[32px] breakpointLg:px-0"
    >
      <div className="grid grid-cols-4 gap-x-8 breakpointMd:grid-cols-6 breakpointLg:grid-cols-12 breakpointLg:gap-8 breakpointXl:grid-cols-12 breakpointXl:gap-8 items-center breakpointLg:items-start">
        <div className="col-span-4 breakpointMd:col-span-6 breakpointLg:col-span-12 breakpointXl:col-span-12">
          <Typography
            content={title}
            variant="headingLg"
            className="headingLg font-bold md:text-[52px]! max-w-(--breakpoint-md) m-auto leading-[120%]! text-left md:text-center pb-4"
            data-testid="simple-accordion-title"
            component="h2"
          />
          <Typography
            content={subTitle}
            variant="bodyMd"
            className="bodyMd lg:bodyLg font-light! text-left md:text-center pb-8 breakpointMd:pb-[48px] lg:pb-12"
            data-testid="simple-accordion-subtitle"
            component="h3"
          />
        </div>
        <div className="hidden breakpointLg:block col-span-5 relative mx-auto aspect-4/5 w-full max-h-[490px] max-w-md breakpointXl:max-w-none">
          <div className="relative h-full w-full">
            {items.map((item, i) => (
              <Scene key={i} item={item} active={i === activeIndex} />
            ))}
          </div>
        </div>
        <div className="col-span-4 sm:col-span-6  lg:col-span-7 breakpointXl:col-start-7 breakpointXl:col-span-6">
          {items.map((item, i) => (
            <AccordionItem
              key={i}
              pos={i}
              item={item}
              open={i === activeIndex}
              onToggle={() => setActiveIndex(i)}
            />
          ))}
        </div>
      </div>
    </Section>
  )
})

function SimpleAccordion({ content }) {
  const { config } = useMemoizedContentGetter(content, ['config'])
  return <SimpleAccordionModule config={config?.jsonContent || {}} />
}

export { SimpleAccordion, SimpleAccordionModule }
export default SimpleAccordion
